import { getName } from "country-list";
import { Confidence, DayNight, IncidentCardLink } from "../../types";
import { normaliseUrl } from "../../../../../formatting/string-manipulation";

const normaliseCountryCode = (countryCode: string): string | null => {
  const trimmedCountryCode = countryCode.trim().toUpperCase();

  if (!trimmedCountryCode) return null;

  return getName(trimmedCountryCode) ?? null;
};

export const compact = (
  values: Array<string | null | undefined>,
): Array<string> =>
  values.filter((value): value is string => Boolean(value && value.trim()));

const mapCountryCodesToNames = (countries: Array<unknown>): Array<string> => {
  return compact(
    countries.map((country) => {
      if (typeof country !== "string") return null;

      return normaliseCountryCode(country);
    }),
  );
};

export const normaliseAffectedCountries = (value: unknown): Array<string> => {
  if (Array.isArray(value)) return mapCountryCodesToNames(value);

  if (typeof value === "string") {
    try {
      const parsedValue: unknown = JSON.parse(value);

      if (Array.isArray(parsedValue))
        return mapCountryCodesToNames(parsedValue);
    } catch {
      return mapCountryCodesToNames(value.split(","));
    }
  }

  return [];
};

export const createLinks = (
  links: Array<IncidentCardLink | null | undefined>,
): Array<IncidentCardLink> =>
  links.filter((link): link is IncidentCardLink =>
    Boolean(link?.href && link.label),
  );

export const createLink = (
  label: string,
  href: string | undefined,
): IncidentCardLink | null => {
  const normalisedHref = normaliseUrl(href);

  if (!normalisedHref) return null;

  return {
    label,
    href: normalisedHref,
  };
};

export const formatValuesToBeCommaSeparated = (
  values: string | Array<string> | null | undefined,
): string => {
  if (Array.isArray(values)) {
    const filteredValues = values.filter((value): value is string =>
      Boolean(value?.trim()),
    );
    return filteredValues.length > 0 ? filteredValues.join(", ") : "-";
  }
  return values?.trim() || "-";
};

export const getConfidenceLabel = (
  confidence: Confidence | undefined,
): string | null => {
  if (confidence === "H") return "high";
  if (confidence === "N") return "nominal";
  if (confidence === "L") return "low";

  return null;
};

export const getDayNightLabel = (
  dayNight: DayNight | undefined,
): string | null => {
  if (dayNight === "D") return "daytime";
  if (dayNight === "N") return "night-time";

  return null;
};
